
import { useState, useEffect, useRef } from 'react'
import { motion } from 'framer-motion'
import { Search, Calendar, Filter } from 'lucide-react'
import { Body } from '@meiuca/ui-components'
import { GameSearch } from './GameSearch'
import { GameFiltersModal, type GameFilters } from './GameFiltersModal'
import { FilterChip } from '@meiuca/ui-components'
import { GameItem } from './GameItem'
import { Game } from '@/types/game'
import { gameService, GameStatus } from '@/services/gameService'
import { formatDateForUI } from '@meiuca/web-foundation'
import { useUserLocation } from '@/hooks/useUserLocation'
import { LocationRequiredEmptyState } from './LocationRequiredEmptyState'

interface SearchGamesListProps {
  onShare?: (game: Game) => void
  onGameClick?: (gameId: string) => void
  onJoinGame?: (gameId: string) => void
}

export function SearchGamesList({ 
  onShare, 
  onGameClick, 
  onJoinGame
}: SearchGamesListProps) {
  console.log('🏗️ SearchGamesList component mounted/re-rendered')
  
  const [searchQuery, setSearchQuery] = useState('')
  const [filters, setFilters] = useState<Partial<GameFilters>>({})
  const [isFiltersOpen, setIsFiltersOpen] = useState(false)
  
  // States para jogos e loading
  const [games, setGames] = useState<Game[]>([])
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)
  
  // Ref para garantir execução única mesmo com StrictMode
  const hasLoadedRef = useRef(false)
  
  // Ref para rastrear se o usuário já fez alguma busca
  const hasSearchedRef = useRef(false)
  
  // Hook para localização do usuário
  const { userLocation, hasLocation, isLoading: locationLoading } = useUserLocation()

  // Load data when component mounts and location is available
  useEffect(() => {
    console.log('🔍 Initial useEffect called - loaded:', loaded, 'loading:', loading, 'hasLoadedRef:', hasLoadedRef.current, 'hasLocation:', hasLocation, 'locationLoading:', locationLoading)
    
    // Aguarda o carregamento da localização
    if (locationLoading) {
      console.log('⏳ Waiting for location to load...')
      return
    }
    
    // Se não tem localização, não carrega jogos (mostrará empty state)
    if (!hasLocation) {
      console.log('📍 No user location available, skipping initial load')
      setLoaded(true)
      return
    }
    
    if (!loaded && !loading && !hasLoadedRef.current) {
      hasLoadedRef.current = true
      console.log('🟢 Loading search games - first time (initial load)')
      const loadData = async () => {
        try {
          setLoading(true)
          console.log('📡 Making API call to searchGames with location...', userLocation)
          
          // Inclui coordenadas da localização do usuário nos filtros
          console.log('🧭 userLocation completa:', userLocation)
          console.log('📍 Coordenadas extraídas:', {
            latitude: userLocation?.coordinate?.latitude,
            longitude: userLocation?.coordinate?.longitude
          })
          
          const filtersWithLocation = {
            ...filters,
            latitude: userLocation?.coordinate?.latitude,
            longitude: userLocation?.coordinate?.longitude
          }
          
          console.log('📦 filtersWithLocation final:', filtersWithLocation)
          
          // Busca jogos ativos (próximos) por padrão - funciona mesmo sem auth
          const searchResults = await gameService.searchGames([GameStatus.ACTIVE], filtersWithLocation)
          setGames(searchResults.sort((a, b) => a.startDate.getTime() - b.startDate.getTime()))
        } catch (error) {
          console.error('❌ Error loading search games:', error)
          // Even if there's an error (like 401), still show empty state instead of infinite loop
          setGames([])
        } finally {
          setLoading(false)
          setLoaded(true) // Always set loaded to true to prevent infinite loop
          console.log('✅ Initial load completed')
        }
      }
      loadData()
    } else {
      console.log('⏭️ Skipping initial load - loaded:', loaded, 'loading:', loading, 'hasLoadedRef:', hasLoadedRef.current)
    }
  }, [locationLoading, hasLocation, userLocation]) // Depend on location state

  // Reload data when filters change (but only if initial load completed and has location)
  useEffect(() => {
    console.log('🔍 Filters useEffect called - loaded:', loaded, 'filters:', filters, 'searchQuery:', searchQuery, 'hasLocation:', hasLocation)
    
    if (!loaded || !hasLocation) {
      console.log('⏭️ Skipping filters reload - not loaded yet or no location')
      return
    }
    
    // Marca que o usuário fez uma busca se há query ou filtros
    if (searchQuery || Object.keys(filters).length > 0) {
      hasSearchedRef.current = true
    }
    
    // Don't reload if filters are empty AND search query is empty AND user never searched (initial state)
    if (Object.keys(filters).length === 0 && !searchQuery && !hasSearchedRef.current) {
      console.log('⏭️ Skipping filters reload - empty filters, no search query, and user never searched')
      return
    }
    
    console.log('🔄 Filters/search changed, reloading search results:', filters, searchQuery)
    
    const reloadData = async () => {
      try {
        setLoading(true)
        console.log('📡 Making API call due to filter/search change...', { filters, searchQuery, userLocation })
        
        // Combina filtros com query de busca e coordenadas
        console.log('🔄 userLocation no reload:', userLocation)
        console.log('🎯 Coordenadas no reload:', {
          latitude: userLocation?.coordinate?.latitude,
          longitude: userLocation?.coordinate?.longitude
        })
        
        const combinedFilters = {
          ...filters,
          query: searchQuery,
          latitude: userLocation?.coordinate?.latitude,
          longitude: userLocation?.coordinate?.longitude
        }
        
        console.log('🔗 combinedFilters final:', combinedFilters)
        
        const searchResults = await gameService.searchGames([GameStatus.ACTIVE], combinedFilters)
        setGames(searchResults.sort((a, b) => a.startDate.getTime() - b.startDate.getTime()))
      } catch (error) {
        console.error('❌ Error reloading search games:', error)
      } finally {
        setLoading(false)
      }
    }
    
    // Debounce para busca, filtros aplicam imediatamente
    const hasSearchQuery = !!searchQuery
    const delayTime = hasSearchQuery ? 500 : 0
    
    const timeoutId = setTimeout(() => {
      reloadData()
    }, delayTime)
    
    return () => clearTimeout(timeoutId)
  }, [filters, searchQuery, loaded, hasLocation, userLocation])


  const handleApplyFilters = (newFilters: GameFilters) => {
    setFilters(newFilters)
  }

  const getActiveFiltersCount = () => {
    let count = 0
    if (filters.modalidade && filters.modalidade.length > 0) count++
    if (filters.dataPreset || filters.dataCustomizada) count++
    if (filters.nivel && filters.nivel.length > 0) count++
    if (filters.ordenacao && filters.ordenacao !== 'data_asc') count++
    return count
  }

  const clearFilter = (type: string, value?: string) => {
    setFilters(prev => {
      const newFilters = { ...prev }
      
      if (type === 'modalidade' && value) {
        newFilters.modalidade = prev.modalidade?.filter(m => m !== value) || []
      } else if (type === 'modalidade') {
        newFilters.modalidade = []
      } else if (type === 'nivel' && value) {
        newFilters.nivel = prev.nivel?.filter(n => n !== value) || []
      } else if (type === 'nivel') {
        newFilters.nivel = []
      } else if (type === 'data') {
        newFilters.dataPreset = ''
        newFilters.dataCustomizada = ''
      } else if (type === 'ordenacao') {
        newFilters.ordenacao = 'data_asc'
      }
      
      return newFilters
    })
  }

  // Se não tem localização, mostra empty state
  if (!locationLoading && !hasLocation) {
    return (
      <div className="space-y-6">
        <LocationRequiredEmptyState 
          title="Localização necessária para buscar jogos"
          description="Para encontrar jogos próximos a você, é necessário selecionar sua localização. Isso nos permite mostrar apenas os jogos relevantes na sua região."
          buttonText="Selecionar minha localização"
        />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Search and Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1">
          <GameSearch onSearch={setSearchQuery} />
        </div>
        <div className="flex gap-2 items-center">
          <button
            onClick={() => setIsFiltersOpen(true)}
            className={`relative p-2 rounded-lg transition-colors ${
              getActiveFiltersCount() > 0 
                ? 'bg-brand-primary text-white' 
                : 'bg-background-white border border-border-primary text-text-secondary hover:bg-background-secondary'
            }`}
            title="Filtros"
          >
            <Filter className="w-5 h-5" />
            {getActiveFiltersCount() > 0 && (
              <span className="absolute -top-1 -right-1 bg-status-error-text text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                {getActiveFiltersCount()}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Active Filters Tags */}
      {getActiveFiltersCount() > 0 && (
        <div className="flex flex-wrap gap-2">
          {/* Modalidade Tags */}
          {filters.modalidade?.map((modalidade: string) => (
            <FilterChip
              key={modalidade}
              label={modalidade}
              onRemove={() => clearFilter('modalidade', modalidade)}
              variant="secondary"
            />
          ))}
          
          {/* Nível Tags */}
          {filters.nivel?.map((nivel: string) => (
            <FilterChip
              key={nivel}
              label={nivel}
              onRemove={() => clearFilter('nivel', nivel)}
              variant="secondary"
            />
          ))}
          
          {/* Data Tag */}
          {(filters.dataPreset || filters.dataCustomizada) && (
            <div className="flex items-center space-x-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
              <span>
                {filters.dataPreset === 'hoje' && 'Hoje'}
                {filters.dataPreset === 'amanha' && 'Amanhã'}
                {filters.dataPreset === 'esta-semana' && 'Esta Semana'}
                {filters.dataPreset === 'customizada' && filters.dataCustomizada && 
                  formatDateForUI(filters.dataCustomizada)}
              </span>
              <button 
                onClick={() => clearFilter('data')}
                className="hover:bg-green-200 rounded-full p-0.5"
              >
                ×
              </button>
            </div>
          )}
          
          {/* Ordenação Tag */}
          {filters.ordenacao && filters.ordenacao !== 'data_asc' && (
            <div className="flex items-center space-x-1 px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
              <span>Data (Decrescente)</span>
              <button 
                onClick={() => clearFilter('ordenacao')}
                className="hover:bg-purple-200 rounded-full p-0.5"
              >
                ×
              </button>
            </div>
          )}
        </div>
      )}


      {/* Games List */}
      <div className="space-y-4">
        {loading ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center justify-center py-12"
          >
            <div className="text-text-secondary">
              Carregando jogos disponíveis...
            </div>
          </motion.div>
        ) : games.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12 bg-background-white rounded-xl border border-border-primary"
          >
            <div className="w-16 h-16 bg-background-secondary rounded-full flex items-center justify-center mx-auto mb-4">
              <Search className="w-8 h-8 text-text-secondary" />
            </div>
            <h2 className="text-lg font-semibold text-text-primary mb-2">
              {searchQuery || Object.keys(filters).length > 0
                ? 'Nenhum jogo encontrado'
                : 'Nenhum jogo disponível'
              }
            </h2>
            <Body className="text-text-secondary">
              {searchQuery || Object.keys(filters).length > 0
                ? 'Tente ajustar sua busca ou filtros'
                : 'Não há jogos disponíveis no momento'
              }
            </Body>
          </motion.div>
        ) : (
          games.map((game, index) => (
            <motion.div
              key={game.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <GameItem 
                game={game} 
                onShare={onShare} 
                onGameClick={onGameClick}
                onJoinGame={onJoinGame}
              />
            </motion.div>
          ))
        )}
      </div>

      {/* Filters Modal */}
      <GameFiltersModal
        isOpen={isFiltersOpen}
        onClose={() => setIsFiltersOpen(false)}
        onApplyFilters={handleApplyFilters}
        initialFilters={filters}
      />
    </div>
  )
}